<template>
	<div>
		<div class="d-flex justify-content-between neue mb16">
			<div class="fsp20 lh28 neue">
				{{ $t('views.Merchant.ChoiseCurrency.1') }}
			</div>
			<Icon name="close" class="c24 pointer" @click="close()" />
		</div>
		<div class="neue-roman" :class="{ 'p24 bg-white rounded12': !isMobile }">
			<!-- Info block -->
			<div class="info-block mb16">
				<Icon name="info" class="c20 info-block__icon" />
				<span class="fsp12 lh16">{{ $t('views.Merchant.ChoiseCurrency.2') }}</span>
			</div>

			<!-- Search -->
			<div class="input-search-wrapper mb16">
				<span class="d-flex justify-content-center align-items-center">
					<Icon name="search-grey" class="c16 mr8" :active="false" />
				</span>
				<input
					class="input-search flex-grow-1 pr4"
					ref="input_search"
					type="text"
					@input="inputSearchHandler"
					tabindex="-1"
					:placeholder="$t('components.service.TableList.1')"
				/>
				<span @click.stop="clearSearchInput" class="pointer">
					<Icon v-if="textInSearch" name="clear-search" class="pe-none c16 d-flex justify-content-center align-items-center" />
				</span>
			</div>

			<!-- Fiat / Non-Fiat tabs -->
			<div class="currency-tabs mb16">
				<button
					type="button"
					class="currency-tabs__btn"
					:class="{ 'currency-tabs__btn--active': activeTab === 'fiat' }"
					@click="activeTab = 'fiat'"
				>
					Fiat
				</button>
				<button
					type="button"
					class="currency-tabs__btn"
					:class="{ 'currency-tabs__btn--active': activeTab === 'non-fiat' }"
					@click="activeTab = 'non-fiat'"
				>
					Non-Fiat
				</button>
			</div>

			<!-- Currency grid -->
			<div class="currency-container" :class="{ 'currency-container--mobile': isMobile }">
				<div class="currency-wrapper" :class="{ 'currency-wrapper--mobile': isMobile }">
					<div v-if="filteredCurrencies.length > 0" class="d-flex flex-wrap gap16">
						<div
							v-for="currency in filteredCurrencies"
							:ref="'currency-' + currency.code"
							class="d-flex align-items-center p16 currency-item gap8"
							:class="{
								'currency-item--mobile': isMobile,
								'currency-item--selected': isCurrencySelected(currency)
							}"
							:key="currency.code"
							@click="choiseCurrency(currency)"
						>
							<span class="currency-logo-wrapper">
								<img v-if="currency.logo" :src="currency.logo" alt="" class="currency-logo" />
								<span v-else class="currency-logo-placeholder">
									<Icon name="default-curr" size="c40" />
								</span>
							</span>
							<span class="d-flex flex-column flex-grow-1">
								<span class="fsp14 lh18 neue">{{ currency.name || currency.code }}</span>
								<span class="fsp12 lh14 primary-grey">{{ currency.network }}</span>
							</span>
							<div
								v-if="isCurrencySelected(currency)"
								@click.stop="clearSelection"
								class="currency-item__close"
							>
								<Icon name="close" :color="{ 0: '#122157' }" class="c14" />
							</div>
						</div>
					</div>
					<div v-else class="d-flex col-12">
						<span class="primary-grey">{{ $t('views.P2P.CheckCurrP2POffers.9') }}</span>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import Icon from '@/components/icons/Icon';

export default {
	components: {
		Icon
	},
	props: {
		close: [Function]
	},
	data() {
		const content = this.$store.state.modal.content;
		return {
			content,
			currencies: content.currencies || [],
			selectedCurrency: content.selectedCurrency || null,
			filteredCurrencies: [],
			textInSearch: false,
			activeTab: content.defaultTab || 'non-fiat'
		};
	},
	computed: {
		isMobile() {
			return this.$store.state.resize.w <= 768;
		}
	},
	watch: {
		activeTab() {
			this.filterCurrencies();
		}
	},
	methods: {
		inputSearchHandler(event) {
			const searchValue = event.target.value.toLowerCase();
			this.textInSearch = searchValue ? true : false;
			this.filterCurrencies(searchValue);
		},
		filterCurrencies(searchValue = '') {
			let filtered = this.currencies.filter(curr => {
				// Filter by tab
				if (this.activeTab === 'fiat' && !curr.isFiat) return false;
				if (this.activeTab === 'non-fiat' && curr.isFiat) return false;
				return true;
			});

			if (searchValue) {
				filtered = filtered.filter(curr => {
					const code = (curr.code || '').toLowerCase();
					const name = (curr.name || '').toLowerCase();
					const network = (curr.network || '').toLowerCase();
					return code.includes(searchValue) || name.includes(searchValue) || network.includes(searchValue);
				});
			}

			this.filteredCurrencies = filtered;
		},
		isCurrencySelected(currency) {
			return this.selectedCurrency === currency.code;
		},
		choiseCurrency(currency) {
			if (this.content.onSelect) {
				this.content.onSelect(currency);
			}
		},
		clearSelection() {
			this.selectedCurrency = null;
			if (this.content.onClear) {
				this.content.onClear();
			}
		},
		clearSearchInput() {
			if (this.$refs.input_search) {
				this.$refs.input_search.value = '';
			}
			this.textInSearch = false;
			this.filterCurrencies();
		},
		scrollToSelected() {
			if (this.selectedCurrency) {
				const refName = 'currency-' + this.selectedCurrency;
				const el = this.$refs[refName];
				if (el && el[0]) {
					el[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
				}
			}
		}
	},
	mounted() {
		this.filterCurrencies();
		this.$nextTick(() => {
			this.scrollToSelected();
		});
	}
};
</script>

<style lang="scss" scoped>
.info-block {
	display: flex;
	align-items: flex-start;
	gap: 10px;
	padding: 12px 14px;
	background: $bg-light-blue;
	border-radius: 8px;
	color: $menu-bg;
}

.info-block__icon {
	flex-shrink: 0;
	margin-top: 2px;
}

.input-search-wrapper {
	display: flex;
	width: 100%;
	background-color: white;
	border: 1px solid $primary-stroke;
	border-radius: 8px;
	position: sticky;
	top: 0;
	left: 0;
	padding: 10px 12px;
	height: 40px;
}

.input-search {
	width: calc(100% - 20px);
	border: none;

	&::placeholder {
		color: $primary-grey !important;
	}
}

.input-search:focus,
.input-search:focus-visible {
	border: none;
	outline: none;
}

.currency-tabs {
	display: flex;
	background: $color-light;
	border-radius: 8px;
	padding: 4px;
}

.currency-tabs__btn {
	flex: 1;
	padding: 8px 16px;
	border: none;
	background: transparent;
	border-radius: 6px;
	font-size: 14px;
	line-height: 18px;
	color: $primary-grey;
	cursor: pointer;
	transition: all 0.2s ease;

	&--active {
		background: #ffffff;
		color: $menu-bg;
		box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
	}
}

.currency-container {
	max-height: 50vh;

	&--mobile {
		max-height: none;
	}
}

.currency-wrapper {
	max-height: 40vh;
	overflow-y: auto;
	overflow-x: hidden;
	padding-right: 4px;

	&::-webkit-scrollbar {
		width: 4px;
		border-radius: 20px;
	}

	&::-webkit-scrollbar-track {
		background: $primary-stroke;
		border-radius: 20px;
	}

	&::-webkit-scrollbar-thumb {
		background-color: $menu-bg;
		border-radius: 20px;
		border: 1px solid $primary-grey;
	}

	&--mobile {
		max-height: none;
	}
}

.currency-item {
	border-radius: 10px;
	cursor: pointer;
	width: calc(50% - 8px);
	border: 1px solid $primary-stroke;
	background: #ffffff;
	transition: all 0.2s ease;

	&:hover {
		border-color: $menu-bg;
	}

	&--mobile {
		width: 100%;
	}

	&--selected {
		background-color: $bg-light-blue;
		border-color: $menu-bg;
	}
}

.currency-logo-wrapper {
	width: 40px;
	height: 40px;
	flex-shrink: 0;
	display: flex;
	align-items: center;
	justify-content: center;
}

.currency-logo {
	width: 40px;
	height: 40px;
	border-radius: 50%;
	object-fit: cover;
}

.currency-logo-placeholder {
	width: 40px;
	height: 40px;
	display: flex;
	align-items: center;
	justify-content: center;

	:deep(svg) {
		width: 40px;
		height: 40px;
	}
}

.currency-item__close {
	width: 20px;
	height: 20px;
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
}
</style>
